import * as signalR from '@microsoft/signalr';

export interface ActivityLogEvent {
  quoteIds: number[];
  orderIds: number[];
  count: number;
}

export type ActivityLogCallback = (event: ActivityLogEvent) => void;

class ActivityLogSignalRService {
  private connection: signalR.HubConnection | null = null;
  private callbacks: Set<ActivityLogCallback> = new Set();
  private isConnecting = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;

  async connect(): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      return;
    }

    if (this.isConnecting) {
      return;
    }

    this.isConnecting = true;

    try {
      this.connection = new signalR.HubConnectionBuilder()
        .withUrl('/updateHub')
        .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
        .configureLogging(signalR.LogLevel.Warning)
        .build();

      this.connection.onreconnecting((error) => {
        console.log('[ActivityLogSignalR] Reconnecting...', error?.message);
      });

      this.connection.onreconnected((connectionId) => {
        console.log('[ActivityLogSignalR] Reconnected:', connectionId);
        this.reconnectAttempts = 0;
      });

      this.connection.onclose((error) => {
        console.log('[ActivityLogSignalR] Connection closed:', error?.message);
        this.handleConnectionClosed();
      });

      this.connection.on('ActivityLogUpdated', (event: ActivityLogEvent) => {
        this.notifyCallbacks(event);
      });

      await this.connection.start();
      console.log('[ActivityLogSignalR] Connected successfully');
      this.reconnectAttempts = 0;
    } catch (error) {
      console.error('[ActivityLogSignalR] Connection failed:', error);
      this.handleConnectionClosed();
    } finally {
      this.isConnecting = false;
    }
  }

  private handleConnectionClosed(): void {
    if (this.reconnectAttempts < this.maxReconnectAttempts && this.callbacks.size > 0) {
      this.reconnectAttempts++;
      const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
      setTimeout(() => this.connect(), delay);
    }
  }

  subscribe(callback: ActivityLogCallback): () => void {
    this.callbacks.add(callback);

    if (this.connection?.state !== signalR.HubConnectionState.Connected && !this.isConnecting) {
      this.connect();
    }

    return () => {
      this.callbacks.delete(callback);
      if (this.callbacks.size === 0) {
        this.disconnect();
      }
    };
  }

  private notifyCallbacks(event: ActivityLogEvent): void {
    this.callbacks.forEach((callback) => {
      try {
        callback(event);
      } catch (error) {
        console.error('[ActivityLogSignalR] Callback error:', error);
      }
    });
  }

  async disconnect(): Promise<void> {
    if (this.connection) {
      try {
        await this.connection.stop();
      } catch (error) {
        console.error('[ActivityLogSignalR] Disconnect error:', error);
      }
      this.connection = null;
    }
  }
}

let serviceInstance: ActivityLogSignalRService | null = null;

export function getActivityLogSignalR(): ActivityLogSignalRService {
  if (!serviceInstance) {
    serviceInstance = new ActivityLogSignalRService();
  }
  return serviceInstance;
}
